import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import MarketingBackdrop, { panelClass, softPanelClass, ctaSectionClass } from "../components/MarketingBackdrop";

const plans = [
  {
    title: "Neo Platform",
    eyebrow: "From",
    price: "R699",
    to: "/#platform",
    subnote: "1-10 active staff / month",
    items: [
      "Clock-ins and attendance",
      "Proof-based duties and workflows",
      "Reports, management dashboard, labor intelligence",
    ],
    footer: "Unique staff who clock in at least once in the month.",
  },
  {
    title: "SCS",
    eyebrow: "Per store / month",
    price: "R499",
    to: "/products/scs",
    items: ["Structured count sessions", "Variance and financial impact", "Clean compare and export workflow"],
  },
  {
    title: "SPS",
    eyebrow: "Per store / month",
    price: "R399",
    to: "/products/sps",
    items: ["Targets and scorecards", "Leaderboards and coaching visibility", "Performance accountability workflow"],
  },
  {
    title: "PLE Pro",
    eyebrow: "Per store / month",
    price: "R699",
    to: "/products/ple",
    items: ["Purchase ledger workflow", "Invoice PDF export", "Manual received and export control"],
  },
];

const neoTiers = [
  { range: "1-10 active staff", price: "R699" },
  { range: "11-25 active staff", price: "R899" },
  { range: "26-50 active staff", price: "R1199" },
  { range: "51+ active staff", price: "Custom" },
];

export default function Pricing() {
  return (
    <MarketingBackdrop>
      <SiteHeader active="pricing" />
      <main className="relative pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="text-xs uppercase tracking-[0.28em] text-cyan-300 mb-3">Pricing</div>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.02]">Clear pricing per store.</h1>
            <p className="mt-5 text-slate-300 text-lg leading-8">
              Start with one free trial store, then roll out live stores when you are ready. Neo scales by active staff per
              store, while SCS, SPS, and PLE stay on simple flat monthly pricing.
            </p>
          </div>

          <section className="mt-12 grid lg:grid-cols-[1.35fr_0.95fr] gap-6 items-start">
            <div className={`${panelClass} p-8 md:p-10`}>
              <div className="text-cyan-300 text-sm uppercase tracking-[0.22em] mb-3">Neo pricing</div>
              <h2 className="text-3xl md:text-4xl font-bold">Neo Platform scales with active staff.</h2>
              <p className="mt-4 text-slate-100/92 leading-8 max-w-3xl">
                You are not charged for every staff profile created. Neo pricing is based on unique staff members who clock in
                at least once during the month at each live store.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {neoTiers.map((tier) => (
                  <div key={tier.range} className={`${softPanelClass} p-5 flex items-center justify-between gap-4`}>
                    <div>
                      <div className="text-slate-100 font-semibold">{tier.range}</div>
                      <div className="text-slate-400 text-sm mt-1">per store / month</div>
                    </div>
                    <div className="text-2xl font-extrabold text-cyan-100">{tier.price}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-3xl border border-cyan-300/15 bg-white/5 p-5 text-slate-300 leading-7">
                Trial usage helps you size the rollout, but trial usage is not billed. Billing only begins once a store goes
                live after trial.
              </div>
            </div>

            <div className={`${softPanelClass} p-8`}>
              <div className="text-cyan-300 text-sm uppercase tracking-[0.22em] mb-3">Billing made simple</div>
              <h2 className="text-3xl font-bold leading-tight">How billing works after trial</h2>
              <div className="mt-6 space-y-5 text-slate-300 leading-8">
                <p>1. Start with a 30-day free trial on one store.</p>
                <p>2. Activate your stores when you are ready to go live.</p>
                <p>3. Use the system normally — billing is calculated from each store’s monthly usage.</p>
                <p>4. You are only billed for stores that are live and in use.</p>
              </div>
              <div className="mt-6 rounded-3xl border border-emerald-300/15 bg-emerald-300/5 p-5 text-emerald-100 leading-7">
                No upfront payments. No surprises. Just fair pricing that scales with your business.
              </div>
            </div>
          </section>

          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mt-12">
            {plans.map((plan) => (
              <Link key={plan.title} to={plan.to} className={`${panelClass} group p-6 hover:-translate-y-1 transition block`}>
                <div className="text-cyan-300 text-sm uppercase tracking-[0.22em]">{plan.eyebrow}</div>
                <div className="text-2xl font-bold mt-3">{plan.title}</div>
                <div className="text-5xl font-extrabold mt-4">{plan.price}</div>
                {plan.subnote ? <div className="mt-3 text-slate-300">{plan.subnote}</div> : null}
                <ul className="mt-6 space-y-3 text-slate-100/92 leading-7">
                  {plan.items.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
                {plan.footer ? <div className="mt-6 text-slate-300 leading-7">{plan.footer}</div> : null}
                <div className="mt-6 text-cyan-200 group-hover:text-cyan-100 font-medium">View product page →</div>
              </Link>
            ))}
          </div>

          <section className="mt-12 grid lg:grid-cols-2 gap-6">
            <div className={`${panelClass} p-8 md:p-10`}>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-cyan-100 mb-4">Best price • Best bundle</div>
              <h2 className="text-3xl md:text-4xl font-bold">Suite Pro - R1499 / store / month</h2>
              <p className="mt-4 text-slate-100/92 leading-8">Includes Neo Platform, SCS, SPS, and PLE Pro in one stack. Best for operators who want one connected operating system instead of separate fixes.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-slate-100/92">
                <div className={`${softPanelClass} p-4`}>One connected operating rhythm</div>
                <div className={`${softPanelClass} p-4`}>Priority rollout path</div>
                <div className={`${softPanelClass} p-4`}>Future-ready module expansion</div>
                <div className={`${softPanelClass} p-4`}>Best value for multi-problem stores</div>
              </div>
              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/book-demo" className="inline-flex items-center bg-[#071120] text-cyan-200 font-semibold px-6 py-3 rounded-full hover:bg-[#0d1d35] transition">Book Suite Pro demo</Link>
                <Link to="/founding-partner" className="inline-flex items-center border border-white/20 bg-white/8 px-6 py-3 rounded-full hover:bg-white/12 transition">See founding partner benefits</Link>
              </div>
            </div>
            <div className={`${softPanelClass} p-8`}>
              <div className="text-cyan-300 text-sm uppercase tracking-[0.22em] mb-3">Founding Partner</div>
              <h2 className="text-3xl font-bold">The early-adopter offer</h2>
              <p className="mt-4 text-slate-300 leading-8">Founding Partner is not the same as a demo. It is an early access relationship for businesses that want locked partner pricing, priority service, and a closer hand in shaping the platform direction.</p>
              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/founding-partner" className="bg-cyan-400 px-6 py-3 rounded-full text-[#071120] font-semibold hover:bg-cyan-300 transition">View benefits</Link>
                <Link to="/faq" className="border border-cyan-400/70 bg-cyan-400/5 px-6 py-3 rounded-full hover:bg-cyan-400/12 transition">Read FAQ</Link>
              </div>
            </div>
          </section>

          <section className={`mt-12 ${ctaSectionClass} p-8 md:p-10 text-center`}>
            <div className="mx-auto mb-3 w-fit rounded-full bg-emerald-400/10 px-4 py-1.5 text-[0.72rem] uppercase tracking-[0.26em] text-emerald-200">B-BBEE procurement advantage</div>
            <p className="max-w-3xl mx-auto text-slate-100 text-lg leading-8">Every R1 spent with MatrixOS counts as R1.35 toward your procurement score. That means your software budget works harder while your teams move into one controlled system.</p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </MarketingBackdrop>
  );
}
